import { DomainEnum } from '@/api';
import { Page } from '@/constants';

export type AccessRule = 'open' | 'blockAvailabilityOnly' | { anyOf: DomainEnum[] };

export const MANAGER_DOMAINS = [DomainEnum.VENDOR_EVENTS, DomainEnum.VENDOR_DASHBOARD];

export const PAGE_RULES: Record<Page, AccessRule> = {
  [Page.Dashboard]: 'open',
  [Page.Calendar]: 'blockAvailabilityOnly',
  [Page.Reports]: 'blockAvailabilityOnly',
  [Page.Availability]: { anyOf: [DomainEnum.VENDOR_EVENTS, DomainEnum.VENDOR_AVAILABILITY] },
  [Page.Menus]: { anyOf: [DomainEnum.VENDOR_EVENTS, DomainEnum.VENDOR_DASHBOARD] },
};

/**
 * Every domain the app fetches for the current user — the single source of
 * truth for both page gating (PermissionsProvider) and the dashboard vendor-list
 * filter (AuthComplete). VENDOR_PANTRY_MANAGER gates no FFR page, but is included
 * so pantry_manager vendors still appear in the vendor list.
 */
export const ALL_PAGE_DOMAINS: DomainEnum[] = [
  ...new Set([
    ...MANAGER_DOMAINS,
    DomainEnum.VENDOR_AVAILABILITY,
    DomainEnum.VENDOR_PANTRY_MANAGER,
    ...Object.values(PAGE_RULES).flatMap((rule) => (typeof rule === 'object' ? rule.anyOf : [])),
  ]),
];

export const isAvailabilityOnly = (domains: Set<DomainEnum>): boolean =>
  domains.has(DomainEnum.VENDOR_AVAILABILITY) &&
  !MANAGER_DOMAINS.some((domain) => domains.has(domain));

export const canAccess = (page: Page, domains: Set<DomainEnum>): boolean => {
  const rule = PAGE_RULES[page];
  if (rule === 'open') return true;
  if (rule === 'blockAvailabilityOnly') return !isAvailabilityOnly(domains);
  return rule.anyOf.some((domain) => domains.has(domain));
};

export const domainsForPage = (page: Page): DomainEnum[] => {
  const rule = PAGE_RULES[page];
  return typeof rule === 'object' ? rule.anyOf : ALL_PAGE_DOMAINS;
};
